import { FileText, ExternalLink, Library, Sparkles, Shield } from "lucide-react";

// Color-code by confidence bucket
function scoreBadgeClass(score) {
  const s = score ?? 0;
  if (s >= 0.65) return "bg-emerald-500/12 text-emerald-700 dark:text-emerald-300 border-emerald-500/30";
  if (s >= 0.45) return "bg-amber-500/12 text-amber-700 dark:text-amber-300 border-amber-500/30";
  return "bg-muted text-muted-foreground border-border";
}

function scoreLabel(score) {
  const s = score ?? 0;
  if (s >= 0.65) return "High";
  if (s >= 0.45) return "Medium";
  return "Low";
}

function formatDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

function OriginBadge({ origin }) {
  if (origin === "global-kb") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-secondary/10 border border-secondary/25 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-secondary">
        <Shield className="w-2.5 h-2.5" /> Official
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-accent/10 border border-accent/25 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-accent">
      <Library className="w-2.5 h-2.5" /> Library
    </span>
  );
}

export function SourceCard({ source, index }) {
  const isGlobal = source.origin === "global-kb";
  const title = source.policy_name || source.doc_title || "Untitled document";
  const insurer = source.insurer_name || source.source_name || "Unknown";
  const docType = source.doc_type;
  const url = source.source_url;
  const pubDate = formatDate(source.publication_date);
  const score = source.score;
  const badgeClass = scoreBadgeClass(score);

  const CardContent = (
    <>
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-1.5 flex-wrap">
          <OriginBadge origin={source.origin} />
          {docType && (
            <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground bg-muted/60 rounded px-1.5 py-0.5">
              {docType.replace(/-/g, " ")}
            </span>
          )}
        </div>
        <span
          className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-semibold ${badgeClass}`}
          title={`Similarity ${Math.round((score || 0) * 100)}%`}
        >
          <Sparkles className="w-2.5 h-2.5" /> {scoreLabel(score)}
        </span>
      </div>
      <div className="text-[13px] font-semibold leading-tight text-foreground line-clamp-2 mb-1" title={title}>
        {title}
      </div>
      <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
        <FileText className="w-3 h-3 shrink-0" />
        <span className="truncate">{insurer}</span>
        {pubDate && (
          <>
            <span aria-hidden>·</span>
            <span className="whitespace-nowrap">{pubDate}</span>
          </>
        )}
      </div>
      {isGlobal && url && (
        <div className="mt-2 pt-2 border-t border-border/60">
          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-secondary group-hover:underline">
            View source <ExternalLink className="w-3 h-3" />
          </span>
        </div>
      )}
    </>
  );

  const baseClass =
    "group block rounded-xl border p-3 transition-all hover:shadow-md " +
    (isGlobal
      ? "bg-secondary/[0.03] border-secondary/25 hover:border-secondary/50 hover:bg-secondary/[0.06]"
      : "bg-accent/[0.04] border-accent/25 hover:border-accent/50 hover:bg-accent/[0.07]");

  if (isGlobal && url) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`source-card-${index}`}
        className={baseClass}
      >
        {CardContent}
      </a>
    );
  }

  return (
    <div data-testid={`source-card-${index}`} className={baseClass}>
      {CardContent}
    </div>
  );
}

export function SourceCardsGrid({ sources }) {
  if (!sources || sources.length === 0) return null;
  const globalCount = sources.filter((s) => s.origin === "global-kb").length;
  const libraryCount = sources.length - globalCount;

  return (
    <div data-testid="sources-grid" className="mt-4 pt-4 border-t border-border/60">
      <div className="flex items-center justify-between mb-2.5">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
          Sources ({sources.length})
        </div>
        <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
          {globalCount > 0 && (
            <span className="inline-flex items-center gap-1">
              <Shield className="w-2.5 h-2.5 text-secondary" /> {globalCount} official
            </span>
          )}
          {libraryCount > 0 && (
            <span className="inline-flex items-center gap-1">
              <Library className="w-2.5 h-2.5 text-accent" /> {libraryCount} your library
            </span>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {sources.map((s, i) => (
          <SourceCard key={`${s.source_url || s.document_id || i}-${i}`} source={s} index={i} />
        ))}
      </div>
    </div>
  );
}
